import { Button } from '@/components/ui/button';

export default function NoteEditModal({
  editingNote,
  setEditingNote,
  onNoteUpdate,
  onNoteDelete,
  onNotesReload,
  onNoteEditComplete,
}) {
  if (!editingNote) return null;

  const closeModal = () => {
    setEditingNote(null);
    if (onNoteEditComplete) {
      onNoteEditComplete();
    }
  };

  const handleSave = async () => {
    const isTempNote = editingNote.id && editingNote.id.toString().startsWith('temp-');

    if (isTempNote) {
      console.warn('Cannot update temp note. Waiting for real note to be created.');
      return;
    }

    if (onNoteUpdate && editingNote.id) {
      try {
        await onNoteUpdate(editingNote.id, {
          heading: editingNote.heading || '',
          content: editingNote.content || '',
          color: editingNote.color || 'yellow',
        });
        closeModal();
      } catch (error) {
        console.error('Error updating note:', error);
      }
      return;
    }

    closeModal();
  };

  const handleDelete = async () => {
    const isTempNote = editingNote.id && editingNote.id.toString().startsWith('temp-');

    if (isTempNote) {
      if (onNotesReload) {
        onNotesReload();
      }
      closeModal();
      return;
    }

    if (onNoteDelete && editingNote.id) {
      try {
        await onNoteDelete(editingNote.id);
        closeModal();
      } catch (error) {
        console.error('Error deleting note:', error);
      }
      return;
    }

    closeModal();
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4" onClick={closeModal}>
      <div
        className="bg-[hsl(var(--card-bg))] border border-[hsl(var(--card-border))] rounded-lg p-3 sm:p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">Edit Note</h3>

        <div className="space-y-3 sm:space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-foreground mb-1">Heading</label>
            <input
              type="text"
              value={editingNote.heading || ''}
              onChange={(e) => setEditingNote({ ...editingNote, heading: e.target.value })}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded text-xs sm:text-sm text-foreground"
              placeholder="Optional heading"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-foreground mb-1">Content</label>
            <textarea
              value={editingNote.content || ''}
              onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 bg-[hsl(var(--main-bg))] border border-[hsl(var(--card-border))] rounded text-xs sm:text-sm text-foreground min-h-[80px] sm:min-h-[100px]"
              placeholder="Note content..."
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-foreground mb-1">Color</label>
            <div className="flex gap-1.5 sm:gap-2">
              {['yellow', 'blue', 'green', 'pink', 'purple'].map((color) => (
                <button
                  key={color}
                  onClick={() => setEditingNote({ ...editingNote, color })}
                  className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full border-2 ${editingNote.color === color ? 'border-white' : 'border-transparent'
                    } ${color === 'yellow' ? 'bg-yellow-400' :
                      color === 'blue' ? 'bg-blue-400' :
                        color === 'green' ? 'bg-green-400' :
                          color === 'pink' ? 'bg-pink-400' :
                            'bg-purple-400'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-4 sm:mt-6">
          <Button
            onClick={handleSave}
            className="flex-1 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--teal-glow))] text-white"
          >
            Save
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            className="text-red-400 border-red-400 hover:bg-red-400/20"
          >
            Delete
          </Button>
          <Button
            onClick={closeModal}
            variant="outline"
            className="bg-[hsl(var(--card-bg))] border-[hsl(var(--card-border))] text-foreground hover:bg-[hsl(var(--sidebar-hover))] hover:text-white"
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
